import type { LocalNotificationsPlugin, PendingLocalNotificationSchema } from '@capacitor/local-notifications';

import { AlarmWithUserAlarm } from 'src/models/alarm';

import { AlarmTranslator } from '../types';
import {
	AlarmNotificationCandidate,
	alarmIdentity,
	createAlarmNotificationCandidate,
	getEffectiveAlarmTimeInSeconds,
	isSupportedAlarmType,
	parseAlarmEpochSeconds,
} from './AlarmNotificationCandidate';

const FOREGROUND_COVERAGE_MINUTES = 1;
const BACKGROUND_COVERAGE_MINUTES = 43_200;
const SCHEDULING_FINGERPRINT_VERSION = 1;

const COVERED_ALARM_TYPES: AlarmWithUserAlarm['type'][] = ['appointment', 'task', 'taskOwner', 'taskOwnerAlways'];

type AlarmAcquisitionSource = {
	readonly caller: string;
	readonly lifecycleTrigger: string;
	readonly query: string;
};

type AlarmAcquisitionDefinition = {
	readonly acquisitionSource: AlarmAcquisitionSource;
	readonly coverageMinutes: number;
};

type AlarmAcquisitionBase = {
	acquisitionSource: AlarmAcquisitionSource;
	coverageUntil: number;
	coveredAlarmTypes: AlarmWithUserAlarm['type'][];
	frozenNow: number;
};

export type SuccessfulAlarmAcquisitionResult = {
	desiredRows: AlarmWithUserAlarm[];
	state: 'success';
} & AlarmAcquisitionBase;

export type FailedAlarmAcquisitionResult = {
	failureCategory: 'query_failure';
	state: 'failure';
} & AlarmAcquisitionBase;

export type AlarmAcquisitionResult = FailedAlarmAcquisitionResult | SuccessfulAlarmAcquisitionResult;

export class NativeAlarmIdCollisionError extends Error {
	public readonly reason = 'native_id_collision' as const;

	public constructor() {
		super('Distinct alarm identities share one native notification ID.');
		this.name = 'NativeAlarmIdCollisionError';
	}
}

export class NativeAlarmIdConflictError extends Error {
	public readonly reason = 'native_id_conflict' as const;

	public constructor() {
		super('A desired native notification ID is occupied by an unowned Pending notification.');
		this.name = 'NativeAlarmIdConflictError';
	}
}

type DesiredAlarmCandidate = {
	alarm: AlarmWithUserAlarm;
	candidate: AlarmNotificationCandidate;
};

type OwnedPendingAlarm = {
	effectiveAt: number;
	fingerprint: {
		effectiveAt: number;
		identity: string;
		version: number;
	} | null;
	type: AlarmWithUserAlarm['type'];
};

const createAcquisitionDefinition = (
	acquisitionSource: AlarmAcquisitionSource,
	coverageMinutes: number
): AlarmAcquisitionDefinition =>
	Object.freeze({
		acquisitionSource: Object.freeze({ ...acquisitionSource }),
		coverageMinutes,
	});

export const FOREGROUND_ALARM_ACQUISITION = createAcquisitionDefinition(
	{
		caller: 'useAlarmStore.fetchAlarms',
		lifecycleTrigger: 'foreground',
		query: 'IFS_Alarm_Query_Queries_GetAllUnconfirmedAlarmsForUserQuery',
	},
	FOREGROUND_COVERAGE_MINUTES
);

export const BACKGROUND_ALARM_ACQUISITION = createAcquisitionDefinition(
	{
		caller: 'useAlarmStore.backgroundFetchAlarms',
		lifecycleTrigger: 'background',
		query: 'IFS_Alarm_Query_Queries_GetAllUnconfirmedBackgroundRefreshAlarmsForUserQuery',
	},
	BACKGROUND_COVERAGE_MINUTES
);

let reconciliationTail: Promise<void> | null = null;
const snapshotCoverageMinutes = new WeakMap<SuccessfulAlarmAcquisitionResult, number>();

export const createSuccessfulAlarmAcquisition = (
	desiredRows: AlarmWithUserAlarm[],
	definition: AlarmAcquisitionDefinition
): SuccessfulAlarmAcquisitionResult => {
	const snapshot: SuccessfulAlarmAcquisitionResult = {
		acquisitionSource: Object.freeze({ ...definition.acquisitionSource }),
		coverageUntil: 0,
		coveredAlarmTypes: [...COVERED_ALARM_TYPES],
		desiredRows: normalizeDesiredRows(desiredRows),
		frozenNow: 0,
		state: 'success',
	};
	snapshotCoverageMinutes.set(snapshot, definition.coverageMinutes);
	return snapshot;
};

export const createFailedAlarmAcquisition = (definition: AlarmAcquisitionDefinition): FailedAlarmAcquisitionResult => {
	const frozenNow = captureFrozenNow();

	return {
		acquisitionSource: Object.freeze({ ...definition.acquisitionSource }),
		coverageUntil: frozenNow + definition.coverageMinutes * 60,
		coveredAlarmTypes: [...COVERED_ALARM_TYPES],
		failureCategory: 'query_failure',
		frozenNow,
		state: 'failure',
	};
};

export const reconcileAlarmNotifications = (
	snapshot: SuccessfulAlarmAcquisitionResult,
	localNotifications: LocalNotificationsPlugin,
	t: AlarmTranslator
): Promise<void> => {
	const reconcile = (): Promise<void> => {
		const cycleSnapshot = materializeSnapshotAuthority(snapshot);
		return reconcileSnapshot(cycleSnapshot, localNotifications, t);
	};
	const cycle = reconciliationTail === null ? reconcile() : reconciliationTail.then(reconcile);
	const guardedCycle = cycle.catch(() => undefined);
	reconciliationTail = guardedCycle;
	void guardedCycle.finally(() => {
		if (reconciliationTail === guardedCycle) {
			reconciliationTail = null;
		}
	});

	return cycle;
};

const materializeSnapshotAuthority = (snapshot: SuccessfulAlarmAcquisitionResult): SuccessfulAlarmAcquisitionResult => {
	const coverageMinutes = snapshotCoverageMinutes.get(snapshot);
	if (coverageMinutes === undefined) {
		throw new Error('Alarm acquisition coverage is unavailable.');
	}

	const frozenNow = captureFrozenNow();
	if (!Object.isFrozen(snapshot)) {
		snapshot.frozenNow = frozenNow;
		snapshot.coverageUntil = frozenNow + coverageMinutes * 60;
		return deepFreeze(snapshot);
	}

	return deepFreeze({
		acquisitionSource: snapshot.acquisitionSource,
		coverageUntil: frozenNow + coverageMinutes * 60,
		coveredAlarmTypes: snapshot.coveredAlarmTypes,
		desiredRows: snapshot.desiredRows,
		frozenNow,
		state: 'success',
	});
};

const reconcileSnapshot = async (
	snapshot: SuccessfulAlarmAcquisitionResult,
	localNotifications: LocalNotificationsPlugin,
	t: AlarmTranslator
): Promise<void> => {
	const desiredCandidates = deepFreeze(createDesiredCandidates(snapshot, t));
	const desiredByNativeId = createDesiredCandidateMap(desiredCandidates);
	const pending = await localNotifications.getPending();
	const pendingByNativeId = groupPendingByNativeId(pending.notifications);
	const nativeIdsToCancel: number[] = [];
	const nativeIdsAlreadyReconciled = new Set<number>();
	const nativeIdsBlockedByUnownedPending = new Set<number>();

	for (const [nativeId, pendingNotifications] of pendingByNativeId) {
		const ownedPending = pendingNotifications.map(readOwnedPendingAlarm);
		if (ownedPending.some((ownedAlarm) => ownedAlarm === null)) {
			if (desiredByNativeId.has(nativeId)) {
				throw new NativeAlarmIdConflictError();
			}

			nativeIdsBlockedByUnownedPending.add(nativeId);
			continue;
		}

		const provenOwnedPending = ownedPending as OwnedPendingAlarm[];
		const desiredCandidate = desiredByNativeId.get(nativeId);
		if (desiredCandidate !== undefined) {
			const onlyPendingAlarm = provenOwnedPending[0];
			if (
				provenOwnedPending.length === 1 &&
				onlyPendingAlarm !== undefined &&
				pendingFingerprintMatchesCandidate(onlyPendingAlarm, desiredCandidate.candidate)
			) {
				nativeIdsAlreadyReconciled.add(nativeId);
			} else {
				nativeIdsToCancel.push(nativeId);
			}
			continue;
		}

		if (provenOwnedPending.every((ownedAlarm) => snapshotCoversPendingAlarm(snapshot, ownedAlarm))) {
			nativeIdsToCancel.push(nativeId);
		}
	}

	const sortedNativeIdsToCancel = [...new Set(nativeIdsToCancel)].sort(compareNativeIds);
	if (sortedNativeIdsToCancel.length > 0) {
		await localNotifications.cancel({
			notifications: sortedNativeIdsToCancel.map((id) => ({ id })),
		});
	}

	const sortedCandidatesToSchedule = desiredCandidates
		.filter(({ candidate }) => {
			const nativeId = candidate.notification.id;
			return !nativeIdsAlreadyReconciled.has(nativeId) && !nativeIdsBlockedByUnownedPending.has(nativeId);
		})
		.sort((first, second) => compareNativeIds(first.candidate.notification.id, second.candidate.notification.id));

	if (sortedCandidatesToSchedule.length > 0) {
		await localNotifications.schedule({
			notifications: sortedCandidatesToSchedule.map(({ candidate }) => candidate.notification),
		});
	}
};

const createDesiredCandidateMap = (desiredCandidates: DesiredAlarmCandidate[]): Map<number, DesiredAlarmCandidate> => {
	const desiredByNativeId = new Map<number, DesiredAlarmCandidate>();
	for (const desiredCandidate of desiredCandidates) {
		const nativeId = desiredCandidate.candidate.notification.id;
		const existingCandidate = desiredByNativeId.get(nativeId);
		if (
			existingCandidate !== undefined &&
			(existingCandidate.candidate.identity !== desiredCandidate.candidate.identity ||
				existingCandidate.candidate.effectiveAt !== desiredCandidate.candidate.effectiveAt)
		) {
			throw new NativeAlarmIdCollisionError();
		}

		desiredByNativeId.set(nativeId, desiredCandidate);
	}

	return desiredByNativeId;
};

const createDesiredCandidates = (
	snapshot: SuccessfulAlarmAcquisitionResult,
	t: AlarmTranslator
): DesiredAlarmCandidate[] => {
	return snapshot.desiredRows.flatMap((alarm) => {
		const candidate = createAlarmNotificationCandidate(alarm, snapshot.frozenNow, t);
		return candidate === null ? [] : [{ alarm, candidate }];
	});
};

const groupPendingByNativeId = (
	pendingNotifications: PendingLocalNotificationSchema[]
): Map<number, PendingLocalNotificationSchema[]> => {
	const grouped = new Map<number, PendingLocalNotificationSchema[]>();
	for (const notification of pendingNotifications) {
		const notifications = grouped.get(notification.id) ?? [];
		notifications.push(notification);
		grouped.set(notification.id, notifications);
	}

	return new Map([...grouped.entries()].sort(([firstId], [secondId]) => compareNativeIds(firstId, secondId)));
};

const readOwnedPendingAlarm = (pending: PendingLocalNotificationSchema): OwnedPendingAlarm | null => {
	const extra = readObject(pending.extra);
	if (extra === null || typeof extra.id !== 'string' || !isSupportedAlarmType(extra.type)) {
		return null;
	}

	const identity = alarmIdentity({ id: extra.id, type: extra.type });
	if (pending.id !== hashCode(identity)) {
		return null;
	}

	if (Object.prototype.hasOwnProperty.call(extra, 'alarmSchedulingFingerprint')) {
		const fingerprint = readObject(extra.alarmSchedulingFingerprint);
		const effectiveAt = parseAlarmEpochSeconds(fingerprint?.effectiveAt);
		if (
			fingerprint === null ||
			fingerprint.version !== SCHEDULING_FINGERPRINT_VERSION ||
			fingerprint.identity !== identity ||
			effectiveAt === null
		) {
			return null;
		}

		return {
			effectiveAt,
			fingerprint: {
				effectiveAt,
				identity,
				version: SCHEDULING_FINGERPRINT_VERSION,
			},
			type: extra.type,
		};
	}

	const effectiveAt = readPublicPendingScheduleTime(pending);
	return effectiveAt === null ? null : { effectiveAt, fingerprint: null, type: extra.type };
};

const readPublicPendingScheduleTime = (pending: PendingLocalNotificationSchema): null | number => {
	const scheduledAt = pending.schedule?.at;
	if (!(scheduledAt instanceof Date)) {
		return null;
	}

	return parseAlarmEpochSeconds(scheduledAt.getTime() / 1000);
};

const pendingFingerprintMatchesCandidate = (
	pending: OwnedPendingAlarm,
	candidate: AlarmNotificationCandidate
): boolean => {
	return (
		pending.fingerprint?.version === SCHEDULING_FINGERPRINT_VERSION &&
		pending.fingerprint.identity === candidate.identity &&
		pending.fingerprint.effectiveAt === candidate.effectiveAt
	);
};

const snapshotCoversPendingAlarm = (
	snapshot: SuccessfulAlarmAcquisitionResult,
	pending: OwnedPendingAlarm
): boolean => {
	return snapshot.coveredAlarmTypes.includes(pending.type) && pending.effectiveAt <= snapshot.coverageUntil;
};

const normalizeDesiredRows = (desiredRows: AlarmWithUserAlarm[]): AlarmWithUserAlarm[] => {
	const normalizedRows = new Map<string, AlarmWithUserAlarm>();
	for (const alarm of desiredRows) {
		const sanitizedAlarm = sanitizeDesiredAlarm(alarm);
		const identity = alarmIdentity(sanitizedAlarm);
		const effectiveAt = getEffectiveAlarmTimeInSeconds(sanitizedAlarm);
		const fingerprintKey = `${identity}#${String(effectiveAt)}`;
		if (!normalizedRows.has(fingerprintKey)) {
			normalizedRows.set(fingerprintKey, sanitizedAlarm);
		}
	}

	return [...normalizedRows.values()];
};

const sanitizeDesiredAlarm = (alarm: AlarmWithUserAlarm): AlarmWithUserAlarm => ({
	alarmTimestamp: alarm.alarmTimestamp,
	id: alarm.id,
	overriddenAlarmTimestamp: alarm.overriddenAlarmTimestamp,
	timestamp: alarm.timestamp,
	title: alarm.title,
	type: alarm.type,
	userAlarmTimestamp: alarm.userAlarmTimestamp ?? null,
});

const captureFrozenNow = (): number => Math.floor(Date.now() / 1000);

const compareNativeIds = (first: number, second: number): number => first - second;

const deepFreeze = <Value>(value: Value, visited = new WeakSet<object>()): Value => {
	if (typeof value !== 'object' || value === null || visited.has(value)) {
		return value;
	}

	visited.add(value);
	const record = value as unknown as Record<PropertyKey, unknown>;
	for (const property of Reflect.ownKeys(record)) {
		deepFreeze(record[property], visited);
	}

	return Object.isFrozen(value) ? value : Object.freeze(value);
};

const readObject = (value: unknown): Record<string, unknown> | null => {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;
};

const hashCode = (value: string): number => {
	return value
		.split('')
		.reduce((previousHash, character) => ((previousHash << 5) - previousHash + character.charCodeAt(0)) | 0, 0);
};
